import { ONBOARDING_PANE, type OnboardingStepPane } from "@/onboarding/routes";
import StAdminAccess from "@/assets/images/st-admin-access.png";
import StAdminDetails from "@/assets/images/st-admin-details.png";
import StAdminEmployees from "@/assets/images/st-admin-employees.png";
import StAdminPermissions from "@/assets/images/st-admin-permissions.png";

/**
 * The guides that sit beside the steps, in our own chrome.
 *
 * They say the part that applies to Volca, in our words, with the provider's own full
 * instructions linked at the end of each section. A customer sent straight to someone
 * else's help centre has to work out which of a dozen articles is theirs and then
 * translate it back to what we asked for.
 *
 * Bodies are Markdown, rendered with the app's shared prose treatment, so they can use
 * tables, quotes and bold interface labels without a bespoke renderer. `{{AP_ADDRESS}}`
 * is replaced with the business's own Volca address.
 */
export const AP_ADDRESS_TOKEN = "{{AP_ADDRESS}}";

/** The mailbox ServiceTitan sends the admin invitation to. Matches Justin's PDF. */
const ST_ADMIN_EMAIL = "support@volca.ai";

export interface GuideChecklist {
  /** Stable, and part of the localStorage key, so renaming it loses people's ticks. */
  id: string;
  /** Resolved against the business's products when the guide is shown. */
  kind: "servicetitan-permissions";
}

export interface Guide {
  id: string;
  title: string;
  /** One line, shown under the title and in the panel. */
  summary: string;
  body: string;
  /** Rendered under the body. Ticks live in the customer's browser and go nowhere. */
  checklist?: GuideChecklist;
}

export const FORWARDING_GUIDE: Guide = {
  id: "forwarding-invoices",
  title: "Forward invoices to Volca",
  summary:
    "A forwarding rule sends a copy of every invoice email to Volca automatically. You set it up once, and you still get every invoice as you do today.",
  body: `Once forwarding is on, Volca works on each invoice as it arrives. Without it, your team has usually handled an invoice before Volca ever sees it.

## Your Volca address

Your address is \`${AP_ADDRESS_TOKEN}\`.

- Volca accepts email from any sender, so forwarded emails and vendor emails both work.
- Use it for vendor invoices, credit memos and receipts only.

## Pick your setup

Most shops get the bulk of their invoices from a handful of vendors. **Start with your biggest vendors**, then add the rest. You don't need every vendor on day one.

| If your invoices | Set up |
| --- | --- |
| Arrive in one shared mailbox, such as ap@ or accounting@ | **A forwarding rule** on that mailbox |
| Arrive in several people's inboxes | A forwarding rule on each of those inboxes, or vendors send directly |
| Come from vendors who can add a second email address | **Vendors send directly** to your Volca address |
| Only appear on a vendor's website | Download the PDFs and email them to your Volca address |

Not sure who runs your mailbox? Send this to whoever manages your company email.

## Outlook or Microsoft 365

Outlook needs no confirmation code. A rule that forwards only invoice emails is best.

**Forward invoice emails with a rule** (new Outlook and Outlook on the web)

1. Select **Settings**, then **Mail > Rules > Add new rule**.
2. Name it, for example "Send invoices to Volca".
3. Add a condition: **Has attachment**. To start with your biggest vendors, add **From** with their addresses.
4. Add an action: **Forward to**, and enter your Volca address.
5. Click **Save**.

**Or forward everything** from a mailbox used only for invoices: **Settings > Mail > Forwarding**, turn on **Enable forwarding**, enter your Volca address, tick **Keep a copy of forwarded messages**, and click **Save**.

Microsoft's full steps: [Use rules to automatically forward messages](https://support.microsoft.com/en-us/outlook/mail/use-rules-to-automatically-forward-messages).

**Rule saved but nothing arrives?** Microsoft 365 often blocks forwarding to outside addresses by default. Ask your IT admin to allow external forwarding for this mailbox.

## Gmail or Google Workspace

About ten minutes, including one code from us.

**Part 1: add Volca as a forwarding address**

1. In Gmail, click the **gear icon**, then **See all settings**.
2. Open the **Forwarding and POP/IMAP** tab and click **Add a forwarding address**.
3. Enter your Volca address, then click **Next** and **Proceed**.
4. Google emails a confirmation code to your Volca address. It comes to us, so we show it to you on the setup step.
5. Back on the same tab, enter the code and click **Verify**.

**Part 2: forward only invoice emails** (recommended)

1. In the Gmail search bar, click the **filter icon**.
2. In **Has the words**, enter \`has:attachment\`. To start with your biggest vendors, also enter their addresses in **From**, separated by \`OR\`.
3. Click **Create filter**, tick **Forward it to**, choose your Volca address, then click **Create filter** again.

Volca reads only the attachments and sets aside anything that isn't an invoice, so a broad filter is safe. You keep every email in your own inbox.

Google's full steps: [Automatically forward Gmail messages](https://support.google.com/mail/answer/10957).

**Don't see Add a forwarding address?** Your Google Workspace admin has blocked forwarding to outside addresses. Ask them to allow it for this mailbox.

## Test your forwarding

Provider verification and the Volca test are separate. A Gmail verification code permits forwarding to your Volca address. It does not confirm that your forwarding rule works.

1. Save and enable your invoice forwarding rule. Keep a copy in your inbox.
2. On the Volca setup page, enter the mailbox where your invoices arrive.
3. Add a separate rule that forwards messages from the **From** address in the test card to the **To** address. Do not add an attachment condition to this test rule.
4. Select **Send test message**. Volca sends a message to your invoice mailbox and checks that your rule sends it back.
5. Wait for **The test message reached Volca**. This confirms that the test message arrived. It does not check your vendor invoice rule. Check that this rule is also enabled and has the correct conditions.

**No result or an error?** Check your invoice mailbox address, the destination in both rules, and that both rules are enabled. Ask your email administrator to check external forwarding restrictions. Correct the problem, then select **Send another test**. If no result appears, contact Volca support. You can continue onboarding and return to these instructions later.

## Ask vendors to send to Volca

Many vendors can email invoices to a second address. Keep your own address on their list, so you still get a copy. Some vendors can only send to one address. For those, use a forwarding rule instead.

Copy this to your vendor or account rep:

> Hi [Vendor name], please add \`${AP_ADDRESS_TOKEN}\` as an invoice recipient for [Your business name], alongside our current address. Please send invoices as PDF attachments rather than links or zip files. Thank you.

## If an invoice doesn't arrive

| Check | Fix |
| --- | --- |
| The address in your rule | It must match your Volca address exactly, ending in @volca-ap.ai |
| The rule is on | Gmail: the filter shows under **Filters and Blocked Addresses**. Outlook: the rule is switched on. |
| An admin block | Your Google Workspace or Microsoft 365 admin may block forwarding outside the company. Ask them to allow it. |
| The invoice is attached | Volca reads attachments only, not links or the email text. Download the PDF and attach it. |
| The file type | PDF, JPG, PNG or TIFF only. Unzip zip files, and save Word or Excel files as PDF. |
| It is a repeat or a statement | Volca skips an invoice it already has and sets statements aside. Both are expected. |
`,
};

// Follows "Volca ServiceTitan Admin Account Setup", Justin's PDF, screen for screen. The
// figures are that document's own, so the guide and the PDF cannot drift into saying
// different things. Update both together.
export const ST_ADMIN_GUIDE: Guide = {
  id: "st-user",
  title: "Set up the Volca admin account",
  summary:
    "A one-time setup in ServiceTitan that gives Volca its own Admin user. Every screen is shown.",
  body: `**The account to create.** A new employee using the email \`${ST_ADMIN_EMAIL}\`.

**The access we need.** That user set to the **Admin** role.

## 1. Open Employees and click Add

Open **Settings**, the gear icon at the top right. In the left menu click **People**, then **Employees** in the drop-down. Click **Add**.

> **Can't see Settings?** You need settings access. Send this guide to whoever runs ServiceTitan at your shop.

![ServiceTitan settings, with People then Employees in the left menu and the Add button above the employee list](${StAdminEmployees})

## 2. Add the employee

Fill in the new employee form as below, then click **Save Changes**.

| Field | What to enter |
| --- | --- |
| Employee Name | Volca |
| Company Position | **Payroll Manager** and **Purchasing Manager** |
| Role | **Admin** |
| Email | \`${ST_ADMIN_EMAIL}\` |
| New Account Status | **Send invitation via email** |

> **Leave everything else blank.** Business Unit, the photo and the phone numbers aren't needed.

![The employee form, with Employee Name set to Volca, Company Position set to Payroll Manager and Purchasing Manager, and Role set to Admin](${StAdminDetails})

![The contact section, with the email filled in and New Account Status set to send an invitation by email, then Save Changes](${StAdminAccess})

ServiceTitan emails the invite straight to us, so there's no password to make up and nothing to send over.

## 3. Set permissions

On the new employee's profile open the **Permissions** tab, click **Select All**, then save.

![The employee profile with the Permissions tab open and the Select All button beside the search box](${StAdminPermissions})

That's it. Come back and tick the box. We'll find your account, set up the connection and approve it ourselves.
`,
};

export const ST_PERMISSIONS_GUIDE: Guide = {
  id: "st-permissions",
  title: "Permissions for a limited user",
  summary: "What the user needs, if you can't give us admin access.",
  body: `Easiest is to grant us admin access. If you can't, the user you make for us needs the permissions below.

They let us set up your API integration and check our work against what you see in ServiceTitan.

> **Setting up MFA?** Please pick TOTP, an authenticator app, not a phone number. We can't get a code sent to someone's mobile.

Tick them off as you go.`,
  checklist: { id: "st-permissions", kind: "servicetitan-permissions" },
};

const ST_APPROVE: Guide = {
  id: "st-approve-connection",
  title: "Approve our ServiceTitan connection",
  summary:
    "We ask ServiceTitan for access to your account. Nothing reaches us until somebody with administrator access approves that request.",
  body: `Only if you made a limited user. With an admin user we approve our own request and you never see this.

## Find your tenant ID

1. In ServiceTitan, open **Settings**, then **Integrations**, then **API Application Access**.
2. Your tenant ID is on that screen, in the box labelled **Your Tenant ID**.
3. Enter it on the ServiceTitan step, so we know which account to connect.

## Approve our request

1. Go back to **Settings > Integrations > API Application Access**.
2. Find **Volca** in the list of applications.
3. Approve it, and leave the permissions as they are.
4. Check that **Enable Access** stays switched on.

Volca won't show up in that list until we have your tenant ID and have sent the request. We'll email you when it's there.

**Can't approve it?** Your account needs **Generate API Application Key** and **Manage API Application Access**. Only an admin has both, so ask an admin on your team to approve it.
`,
};

export const GUIDES: readonly Guide[] = [
  FORWARDING_GUIDE,
  ST_ADMIN_GUIDE,
  ST_APPROVE,
];

/**
 * Which guides sit beside which step. A step with none shows no panel at all, rather
 * than an empty one, so the panel's presence means there is something to read.
 */
export const STEP_GUIDES: Partial<
  Record<OnboardingStepPane, readonly Guide[]>
> = {
  [ONBOARDING_PANE.invoiceUpload]: [FORWARDING_GUIDE],
  [ONBOARDING_PANE.stAccess]: [
    ST_ADMIN_GUIDE,
    ST_PERMISSIONS_GUIDE,
    ST_APPROVE,
  ],
};
